// Server-time synchronization and countdown hook.
// offset = server_now_ms - Date.now(); median of last 5 samples.
// useCountdown renders deadline_ms - (Date.now() + offset) recomputed
// on a 250ms tick. Never decrements a local counter — throttled tabs
// drift.

import { useEffect, useRef, useState } from 'react'

const MAX_SAMPLES = 5
const TICK_MS = 250

const samples: number[] = []

/**
 * Record a server_now_ms sample from a cable event or HTTP response.
 * The offset is the median of the last MAX_SAMPLES samples.
 */
export function recordServerTime(serverNowMs: number): void {
  const offset = serverNowMs - Date.now()
  samples.push(offset)
  if (samples.length > MAX_SAMPLES) {
    samples.shift()
  }
}

/**
 * Current best estimate of (server_now_ms - Date.now()).
 */
export function getServerOffset(): number {
  if (samples.length === 0) return 0
  const sorted = [...samples].sort((a, b) => a - b)
  return sorted[Math.floor(sorted.length / 2)]
}

/**
 * Returns the current server time estimate in ms.
 */
export function serverNow(): number {
  return Date.now() + getServerOffset()
}

export interface CountdownState {
  /** Remaining milliseconds, floored to 0. null when no deadline. */
  remainingMs: number | null
  /** Whether the clock has hit zero. */
  expired: boolean
}

/**
 * Hook that ticks every 250ms, computing remaining time from the
 * server-synced deadline. Never uses a local decrementing counter.
 */
export function useCountdown(deadlineMs: number | null): CountdownState {
  const [state, setState] = useState<CountdownState>(() => compute(deadlineMs))
  const deadlineRef = useRef(deadlineMs)
  deadlineRef.current = deadlineMs

  useEffect(() => {
    // Immediately compute on deadline change
    setState(compute(deadlineMs))

    if (deadlineMs == null) return

    const id = setInterval(() => {
      setState(compute(deadlineRef.current))
    }, TICK_MS)

    return () => clearInterval(id)
  }, [deadlineMs])

  return state
}

/**
 * The countdown's whole arithmetic, with "now" injected. Pulled out of
 * compute() so it can be asserted on directly — the hook itself needs a
 * renderer and there is no DOM test environment here.
 */
export function countdownFrom(deadlineMs: number | null, nowMs: number): CountdownState {
  if (deadlineMs == null) {
    return { remainingMs: null, expired: false }
  }
  const remaining = Math.max(0, deadlineMs - nowMs)
  return { remainingMs: remaining, expired: remaining === 0 }
}

function compute(deadlineMs: number | null): CountdownState {
  return countdownFrom(deadlineMs, serverNow())
}
